import React, { useCallback, useEffect, useMemo, useState } from "react";
import AxiosInstance from "../../AxiosInstance";
import moment from "moment";
import { useUserTimezoneFormatter } from "../../../components/UserTimezoneDateFormatter/UserTimezoneDateFormatter";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { handleAuth } from "../../../components/Login/Auth";
import "./style.css";
import "./CustomerAppointmentsJobber.css";
import { Box, Button as MuiButton, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  getVisitAppointmentStatusTone,
  JobberAssignedWorkersCell,
  JobberListCard,
  JobberPagination,
  JobberSearch,
  JobberSorting,
  JobberStatusBadge,
  JobberTable,
  JobberTableDocRefTitleCell,
  jobberVisitAssignedWorkersLabel,
  jobberVisitWorkerProfiles,
} from "../../../components/MuiTable/index.jsx";
import { useUserType } from "../../../contexts/UserTypeContext.jsx";

/** Pastel View — mobile collapse Actions row; desktop column hidden via CSS */
const APPT_VIEW_BG = "#E0E7FF";
const APPT_VIEW_COLOR = "#1E3A8A";

const customerApptMobileActionBtnBase = {
  width: "100%",
  minWidth: 0,
  alignSelf: "stretch",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.7rem",
  borderRadius: "8px",
  minHeight: 32,
  px: 0.5,
  py: 0.375,
  lineHeight: 1.15,
  boxShadow: "none",
  whiteSpace: "nowrap",
  justifyContent: "center",
  "& .MuiButton-startIcon": { mr: 0.25, ml: -0.1 },
};

function mergeVisitBuckets({ today = [], upcoming = [], past = [] }) {
  const map = new Map();
  const add = (v) => {
    const id = v?.VisitId;
    if (id == null) return;
    if (!map.has(id)) map.set(id, v);
  };
  [...today, ...upcoming, ...past].forEach(add);
  return Array.from(map.values());
}

function formatTimeForVisitDisplay(timeVal) {
  if (!timeVal) return null;
  const timeStr = String(timeVal).trim();
  if (!timeStr) return null;
  try {
    if (moment(timeVal, moment.ISO_8601, true).isValid() && /T/.test(timeStr)) {
      return moment(timeVal).format("hh:mm A");
    }
    if (timeStr.match(/[AaPp][Mm]/)) {
      const timeMoment = moment(
        timeStr,
        ["h:mm A", "hh:mm A", "H:mm A", "HH:mm A", "h:mm:ss A", "hh:mm:ss A"],
        true
      );
      return timeMoment.isValid() ? timeMoment.format("hh:mm A") : null;
    }
    const timeParts = timeStr.split(":");
    if (timeParts.length >= 2) {
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);
      if (
        !Number.isNaN(hours) &&
        !Number.isNaN(minutes) &&
        hours >= 0 &&
        hours < 24 &&
        minutes >= 0 &&
        minutes < 60
      ) {
        return moment().hour(hours).minute(minutes).second(0).format("hh:mm A");
      }
    }
    const parsed = moment(timeStr);
    return parsed.isValid() ? parsed.format("hh:mm A") : null;
  } catch {
    return null;
  }
}

/** Scheduled appointment calendar date only (no time). */
function formatAppointmentDateDisplay(visit, formatDateWithUserTimezone, dateFormat) {
  if (!visit?.StartDate) return "—";
  let out = formatDateWithUserTimezone(visit.StartDate, {
    dateFormat: dateFormat || "MM/DD/YYYY",
    includeSeconds: false,
    excludeTime: true,
  });
  if (!out || out === "-" || out === "—") return out;
  out = out.replace(/\s+\d{1,2}:\d{2}(:\d{2})?(\s*[AaPp][Mm])?$/i, "").trim();
  return out || "—";
}

function visitSortTimestamp(v) {
  const d = moment(v?.StartDate);
  if (!d.isValid()) return 0;
  const base = d.clone().startOf("day").valueOf();
  const start = formatTimeForVisitDisplay(v?.StartTime);
  if (!start) return base;
  const m = moment(start, "hh:mm A", true);
  if (!m.isValid()) return base;
  return base + (m.hour() * 3600 + m.minute() * 60 + m.second()) * 1000;
}

/** One label for UI, filters, and sort: Completed → Confirmed → time bucket (`Status`). */
function resolveLatestCustomerStatusLabel(visit) {
  const st = String(visit?.Status ?? "").trim();
  const stLower = st.toLowerCase();
  if (visit?.IsComplete === true || stLower === "completed") return "Completed";
  if (
    visit?.IsConfirm === true ||
    visit?.IsConfirmByWorker === true ||
    (Array.isArray(visit?.ConfirmWorker) && visit.ConfirmWorker.length > 0) ||
    st === "Confirmed"
  ) {
    return "Confirmed";
  }
  return st;
}

function customerVisitAssignedWorkerDisplay(visit) {
  return jobberVisitAssignedWorkersLabel(visit);
}

function visitMatchesSearch(visit, rawQuery) {
  const q = (rawQuery || "").trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    String(visit?.StartDate ?? ""),
    visit?.Status ?? "",
    resolveLatestCustomerStatusLabel(visit),
    visit?.ItemName ?? "",
    visit?.ContractData?.Title ?? "",
    String(visit?.ContractData?.ContractNumber ?? ""),
    visit?.ContractData?.Description ?? "",
    visit?.Location?.Address ?? "",
    visit?.Location?.City ?? "",
    visit?.Location?.State ?? "",
    String(visit?.StartTime ?? ""),
    String(visit?.EndTime ?? ""),
    customerVisitAssignedWorkerDisplay(visit),
  ]
    .join(" ")
    .toLowerCase();
  return q
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => haystack.includes(w));
}

function statusRank(s) {
  switch (String(s || "")) {
    case "Today":
      return 1;
    case "Upcoming":
      return 2;
    case "Scheduled":
      return 3;
    case "Late":
      return 4;
    case "Confirmed":
      return 5;
    case "Completed":
      return 6;
    default:
      return 50;
  }
}

function compareVisits(a, b, field, order) {
  const mul = order === "asc" ? 1 : -1;
  switch (field) {
    case "WorkerName": {
      const va = customerVisitAssignedWorkerDisplay(a).toLowerCase();
      const vb = customerVisitAssignedWorkerDisplay(b).toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "StartDate": {
      const va = visitSortTimestamp(a);
      const vb = visitSortTimestamp(b);
      return (va - vb) * mul;
    }
    case "ContractNumber": {
      const va = parseInt(a?.ContractData?.ContractNumber, 10) || 0;
      const vb = parseInt(b?.ContractData?.ContractNumber, 10) || 0;
      return (va - vb) * mul;
    }
    case "ContractTitle": {
      const va = (a?.ContractData?.Title || a?.ItemName || "").toLowerCase();
      const vb = (b?.ContractData?.Title || b?.ItemName || "").toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "VisitTitle": {
      const va = (a?.ItemName || "").toLowerCase();
      const vb = (b?.ItemName || "").toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "Status": {
      const ra = statusRank(resolveLatestCustomerStatusLabel(a));
      const rb = statusRank(resolveLatestCustomerStatusLabel(b));
      if (ra !== rb) return (ra - rb) * mul;
      return (visitSortTimestamp(a) - visitSortTimestamp(b)) * mul;
    }
    default:
      return 0;
  }
}

/** Toolbar options match the single status chip (latest lifecycle label). */
function visitMatchesCustomerStatusToolbar(visit, filterLabel) {
  if (!filterLabel || filterLabel === "All") return true;
  return resolveLatestCustomerStatusLabel(visit) === filterLabel;
}

const CUSTOMER_VISIT_STATUS_DROPDOWN_ITEMS = [
  { text: "All" },
  { text: "Today" },
  { text: "Upcoming" },
  { text: "Late" },
  { text: "Scheduled" },
  { text: "Confirmed" },
  { text: "Completed" },
];

function visitStatusBadge(label) {
  if (!label) return null;
  const text = String(label).trim();
  if (!text) return null;
  return (
    <JobberStatusBadge tone={getVisitAppointmentStatusTone(text)}>
      {text}
    </JobberStatusBadge>
  );
}

const HEADER_DATA = [
  { label: "Assigned Worker", field: "WorkerName", wrap: true },
  { label: "Contract #", field: "ContractNumber", wrap: true },
  { label: "Visit", field: "VisitTitle", wrap: true },
  { label: "Appointment Date", field: "StartDate" },
  { label: "Status", field: "Status" },
  { label: "Actions", field: "" },
];

function Quotes() {
  const navigate = useNavigate();
  const location = useLocation();
  const { CompanyUrl } = useParams();
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  const [loader, setLoader] = useState(false);
  const [quotesData, setQuotesData] = useState({ today: [], upcoming: [], past: [] });
  const [tokenDecode, setTokenDecode] = useState({});
  const [DateDecode, setDateDecode] = useState("MM/DD/YYYY");
  const formatDateWithUserTimezone = useUserTimezoneFormatter();

  const [search, setSearch] = useState("");
  const [visitStatusFilter, setVisitStatusFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState("Status");
  const [sortOrder, setSortOrder] = useState("asc");

  const handleVisitStatusSelect = useCallback((text) => {
    setVisitStatusFilter(text || "All");
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const res = await handleAuth(navigate, location);
      setTokenDecode(res?.data);
      setDateDecode(res?.themes?.Format || "MM/DD/YYYY");
    } catch (error) {
      console.error("Error fetching token decode data:", error);
      setDateDecode("MM/DD/YYYY");
    }
  }, [navigate, location]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const CompanyId = tokenDecode?.CompanyId;
  const CustomerId = tokenDecode?.UserId;

  useEffect(() => {
    if (CompanyId && CustomerId) {
      setLoader(true);
      AxiosInstance.get(`/v1/visit/customer_visits/${CompanyId}/${CustomerId}`)
        .then((response) => {
          if (response?.data?.statusCode === 200) {
            const data = response?.data?.data;
            if (data?.today && data?.upcoming && data?.past) {
              setQuotesData({
                today: data.today || [],
                upcoming: data.upcoming || [],
                past: data.past || [],
              });
            } else if (Array.isArray(data)) {
              const categorizeVisits = (visits) => {
                const todayVisits = [];
                const upcomingVisits = [];
                const pastVisits = [];
                visits.forEach((visit) => {
                  if (visit.Status === "Today") todayVisits.push(visit);
                  else if (visit.Status === "Upcoming") upcomingVisits.push(visit);
                  else if (visit.Status === "Late" || visit.Status === "Scheduled") {
                    pastVisits.push(visit);
                  } else {
                    const visitDate = moment(visit?.StartDate);
                    const today = moment().startOf("day");
                    if (visitDate?.isSame(today, "day")) todayVisits.push(visit);
                    else if (visitDate?.isAfter(today)) upcomingVisits.push(visit);
                    else pastVisits.push(visit);
                  }
                });
                return { today: todayVisits, upcoming: upcomingVisits, past: pastVisits };
              };
              setQuotesData(categorizeVisits(data));
            } else {
              setQuotesData({ today: [], upcoming: [], past: [] });
            }
          } else {
            setQuotesData({ today: [], upcoming: [], past: [] });
          }
        })
        .catch((error) => {
          console.error("API error:", error);
          setQuotesData({ today: [], upcoming: [], past: [] });
        })
        .finally(() => setLoader(false));
    }
  }, [CompanyId, CustomerId]);

  const merged = useMemo(
    () =>
      mergeVisitBuckets({
        today: quotesData?.today,
        upcoming: quotesData?.upcoming,
        past: quotesData?.past,
      }),
    [quotesData]
  );

  const statusToolbarFiltered = useMemo(
    () => merged.filter((v) => visitMatchesCustomerStatusToolbar(v, visitStatusFilter)),
    [merged, visitStatusFilter]
  );

  const filtered = useMemo(
    () => statusToolbarFiltered.filter((v) => visitMatchesSearch(v, search)),
    [statusToolbarFiltered, search]
  );

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => compareVisits(a, b, sortField, sortOrder));
    return list;
  }, [filtered, sortField, sortOrder]);

  const countData = sorted.length;

  const pageSlice = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage]
  );

  useEffect(() => {
    setPage(0);
  }, [search, visitStatusFilter]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(sorted.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [sorted.length, rowsPerPage, page]);

  const baseUrl = CompanyUrl || "defaultcompany";
  const navigatePath = `/${baseUrl}/customers/appointment-confirm`;
  const navigats = [...(location?.state?.navigats || []), "/appointment-confirm"];

  const handleViewVisit = useCallback(
    (visitId, contractId) => {
      if (visitId == null) return;
      const newPath = navigatePath.split("/");
      const baseState = {
        id: visitId,
        navigats: [...navigats, `/${newPath[newPath.length - 1]}`],
      };
      const extra = contractId != null ? { contractId } : {};
      navigate(navigatePath, { state: { ...baseState, ...extra } });
    },
    [navigatePath, navigats, navigate]
  );

  const goToContractDetail = useCallback(
    (e, contractId) => {
      if (e) e.stopPropagation();
      if (!contractId) return;
      const path = `/${baseUrl}/customers/contract-details`;
      const newPath = path.split("/");
      const navigatsContract = location?.state?.navigats
        ? [...location.state.navigats, "/contract-details"]
        : ["/contract-details"];
      navigate(path, {
        state: {
          id: contractId,
          navigats: [...navigatsContract, `/${newPath[newPath.length - 1]}`],
        },
      });
    },
    [baseUrl, navigate, location?.state?.navigats]
  );

  const cellData = useMemo(
    () =>
      pageSlice.map((visit) => {
        const appointmentDateDisplay = formatAppointmentDateDisplay(
          visit,
          formatDateWithUserTimezone,
          DateDecode
        );

        const contractTitle = visit?.ContractData?.Title || visit?.ItemName || "—";
        const visitTitle = visit?.ItemName || "—";
        const num = visit?.ContractData?.ContractNumber;
        const vid = visit?.VisitId;
        const cid = visit?.ContractId;
        const profiles = jobberVisitWorkerProfiles(visit);
        const customerWorkerSeed =
          profiles.length === 1
            ? profiles[0]?.UserId ?? profiles[0]?.WorkerId ?? visit?.VisitId
            : visit?.AssignPerson?.UserId ?? visit?.VisitId;
        const contractLinkColor = isDonationMode ? "#29B3E2" : "#2563EB";

        const statusCell = visitStatusBadge(resolveLatestCustomerStatusLabel(visit));

        return {
          key: vid,
          contractId: cid,
          value: [
            <JobberAssignedWorkersCell
              key={`wk-${vid}`}
              rowKey={String(vid ?? "")}
              workerProfiles={profiles}
              fallbackName={jobberVisitAssignedWorkersLabel(visit)}
              fallbackColorSeed={customerWorkerSeed}
            />,
            <JobberTableDocRefTitleCell
              key={`doc-${vid}`}
              refNumber={num}
              onRefClick={(e) => goToContractDetail(e, cid)}
              linkColor={contractLinkColor}
              title={contractTitle}
              titleLineBreakStyle="none"
              wordsPerLine={0}
            />,
            <Typography
              key={`vt-${vid}`}
              sx={{
                fontSize: "0.9375rem",
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                lineHeight: 1.5,
              }}
            >
              {visitTitle}
            </Typography>,
            <Typography key={`ap-${vid}`} sx={{ whiteSpace: "nowrap", fontSize: "0.9375rem" }}>
              {appointmentDateDisplay}
            </Typography>,
            statusCell,
            <Box
              key={`act-${vid}`}
              className="customer-appt-mobile-actions customer-list-actions-mobile"
              sx={{ display: "grid", width: "100%", maxWidth: "100%" }}
            >
              <MuiButton
                type="button"
                size="small"
                variant="contained"
                disableElevation
                fullWidth
                startIcon={
                  <VisibilityOutlinedIcon
                    sx={{
                      fontSize: 16,
                      color: isDonationMode ? "#0f766e" : APPT_VIEW_COLOR,
                    }}
                  />
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewVisit(vid, cid);
                }}
                sx={{
                  ...customerApptMobileActionBtnBase,
                  backgroundColor: isDonationMode
                    ? "rgba(104, 178, 203, 0.2)"
                    : APPT_VIEW_BG,
                  color: isDonationMode ? "#0f766e" : APPT_VIEW_COLOR,
                  border: isDonationMode
                    ? "1px solid rgba(41, 179, 226, 0.3)"
                    : "1px solid rgba(30, 58, 138, 0.2)",
                  "&:hover": {
                    backgroundColor: isDonationMode
                      ? "rgba(104, 178, 203, 0.32)"
                      : "#c7d2fe",
                    borderColor: isDonationMode
                      ? "rgba(41, 179, 226, 0.45)"
                      : "rgba(30, 58, 138, 0.32)",
                  },
                }}
              >
                View
              </MuiButton>
            </Box>,
          ],
        };
      }),
    [pageSlice, DateDecode, formatDateWithUserTimezone, handleViewVisit, isDonationMode, goToContractDetail]
  );

  const appointmentNavigateState = useCallback(
    (row) => (row?.contractId != null ? { contractId: row.contractId } : {}),
    []
  );

  const jobberCustomerApptMobileProps = {
    showMobileViewDetailsButton: false,
    actionColumnIndex: 5,
    mobileCardPrimaryColumnIndex: 0,
    mobileCardSubtitleColumnIndex: 3,
    mobileCardStatusColumnIndex: 4,
  };

  return (
    <Box
      className="appointments-jobber-table-wrap customer-appointments-jobber-wrap"
      sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}
    >
      <JobberListCard
        title="Your Appointments"
        totalCount={countData}
        addButton={null}
        toolbar={
          <Box className="quotesearch gap-2 d-flex customer_searchBar searchBarOfTable">
            <JobberSorting
              triggerVariant="light"
              dropdownItems={CUSTOMER_VISIT_STATUS_DROPDOWN_ITEMS}
              placeholder="Select Status"
              value={visitStatusFilter}
              onSelect={handleVisitStatusSelect}
            />
            <JobberSearch search={search} setSearch={setSearch} />
          </Box>
        }
        loader={loader}
        footer={
          <JobberPagination
            totalData={countData}
            currentData={rowsPerPage}
            dataPerPage={rowsPerPage}
            pageItems={[10, 25, 50]}
            page={page}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        }
      >
        <JobberTable
          headerData={HEADER_DATA}
          cellData={cellData}
          {...jobberCustomerApptMobileProps}
          setSortField={setSortField}
          setSortOrder={setSortOrder}
          sortOrder={sortOrder}
          sortField={sortField}
          isCollapse={false}
          isNavigate
          navigatePath={navigatePath}
          navigats={navigats}
          getNavigateState={appointmentNavigateState}
        />
      </JobberListCard>
    </Box>
  );
}

export default Quotes;
